import React, { useEffect, useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "../../styles/ag-grid-custom.css";
import { ArrowLeft, User, Search } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/AuthProvider";
import { useTrainingStore } from "../../store/store";
import { Training, Trainee, Test } from "../../types/api";
import { useTrainingModule } from "./TrainingModuleContext";
import axiosInstance from "../../common/axiosInstance";

interface CustomUser {
  id?: string;
  roles?: string[];
}

interface TraineeRow {
  traineeId: string;
  traineeEmployeeId: string;
  name: string;
  preTestScore: number | null;
  postTestScore: number | null;
  attendance: Record<string, "Present" | "Absent">;
}

type TestCategory = "Pre-Test" | "Post-Test";

const formatDate = (dateString?: string | null) => {
  if (!dateString) return "Not Scheduled";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "Not Scheduled";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

// Demo/proposal screen: consolidates topic + test-category selection,
// attendance marking, and offline-answer submission into one place, so a
// trainer doesn't need a training to show up in a specific session table
// (Current/Upcoming/Past) before they can act on it. Deliberately additive
// — it does not change any existing session-table filtering logic.
const TrainerStartTest: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth() as { user: CustomUser };
  const { moduleType, validCategories, paths } = useTrainingModule();
  const isFixedCategory = validCategories.length === 1;
  const { trainings, fetchTrainings, updateAttendance } = useTrainingStore();

  const userRoles = user?.roles || [];
  const isOrgAdmin = userRoles.includes("orgadmin");
  const isTrainerRole = isOrgAdmin || userRoles.includes("orguser");

  const [selectedTrainingId, setSelectedTrainingId] = useState("");
  const [selectedCategory, setSelectedCategory] =
    useState<TestCategory>("Pre-Test");
  const [showAttendancePopup, setShowAttendancePopup] = useState(false);
  const [showOfflineModal, setShowOfflineModal] = useState(false);
  const [offlineTraineeSearch, setOfflineTraineeSearch] = useState("");
  const [attendance, setAttendance] = useState<
    Record<string, "Present" | "Absent">
  >({});

  useEffect(() => {
    if (!isTrainerRole) {
      navigate(paths.dashboard);
    }
  }, [isTrainerRole, navigate, paths.dashboard]);

  useEffect(() => {
    fetchTrainings({
      type: "all",
      where: isFixedCategory
        ? { moduleType, category: validCategories[0] }
        : { moduleType },
    });
  }, [fetchTrainings, moduleType, isFixedCategory, validCategories]);

  // Org admins see every training in this module; trainers only see
  // trainings they're actually assigned to instruct, so they can't pick
  // (and act on) another trainer's session.
  const availableTrainings = useMemo(() => {
    const scoped = (trainings || []).filter(
      (t) =>
        t?.moduleType === moduleType ||
        (isFixedCategory && t?.category === validCategories[0]),
    );
    if (isOrgAdmin) return scoped;
    return scoped.filter((t) =>
      t.instructor?.some((inst) => inst.trainerId === user?.id),
    );
  }, [trainings, moduleType, isFixedCategory, validCategories, isOrgAdmin, user?.id]);

  const selectedTraining: Training | undefined = useMemo(
    () => availableTrainings.find((t) => t.id === selectedTrainingId),
    [availableTrainings, selectedTrainingId],
  );

  const selectedTest: Test | undefined = useMemo(
    () =>
      selectedTraining?.tests?.find(
        (t) => t.testCategory === selectedCategory,
      ),
    [selectedTraining, selectedCategory],
  );

  const traineeRows: TraineeRow[] = useMemo(() => {
    if (!selectedTraining) return [];
    return (selectedTraining.trainees || []).map((trainee: Trainee) => ({
      traineeId: trainee.traineeId,
      traineeEmployeeId: trainee.traineeEmployeeId,
      name: trainee.name,
      preTestScore: trainee.results?.preTestScore ?? null,
      postTestScore: trainee.results?.postTestScore ?? null,
      attendance: {
        "Pre-Test": trainee.attendance?.["Pre-Test"] || "Absent",
        "Post-Test": trainee.attendance?.["Post-Test"] || "Absent",
      },
    }));
  }, [selectedTraining]);

  // Reset the attendance draft whenever the training or category changes,
  // seeded from that category's currently-saved attendance.
  useEffect(() => {
    setAttendance(
      traineeRows.reduce(
        (acc, trainee) => ({
          ...acc,
          [trainee.traineeId]: trainee.attendance[selectedCategory],
        }),
        {} as Record<string, "Present" | "Absent">,
      ),
    );
  }, [traineeRows, selectedCategory]);

  const toggleAttendance = (traineeId: string, status: "Present" | "Absent") => {
    setAttendance((prev) => ({ ...prev, [traineeId]: status }));
  };

  const markAll = (status: "Present" | "Absent") => {
    setAttendance((prev) => {
      const next = { ...prev };
      traineeRows.forEach((trainee) => {
        next[trainee.traineeId] = status;
      });
      return next;
    });
  };

  const sendEmailsToPresentTrainees = async () => {
    if (!selectedTraining || !selectedTest) return;

    const presentTrainees = traineeRows.filter(
      (trainee) => attendance[trainee.traineeId] === "Present",
    );
    if (presentTrainees.length === 0) return;

    const loginUrl = `${window.location.origin}/url/login`;
    const templateFileName =
      selectedCategory === "Pre-Test"
        ? "pretest_notification.json"
        : "posttest_notification.json";

    const fullTrainees = selectedTraining.trainees || [];

    await Promise.all(
      presentTrainees.map(async (row) => {
        const trainee = fullTrainees.find(
          (t) => t.traineeId === row.traineeId,
        );
        if (!trainee?.email) return;
        try {
          const pdfResponse = await axiosInstance.get(
            `/api/test/${selectedTest.id}/generate-pdf`
          );
          const pdfLink = pdfResponse.data.message;

          await axiosInstance.post(
            `/api/sendemail`,
            {
              templateFileName,
              to: [trainee.email],
              cc: [],
              bcc: [],
              variables: [
                { key: "traineeName", value: trainee.name },
                { key: "topic", value: selectedTraining.topic },
                { key: "testCategory", value: selectedCategory },
                { key: "date", value: formatDate(selectedTraining.date) },
                { key: "testLink", value: loginUrl },
                { key: "pdfLink", value: pdfLink },
              ],
            }
          );
        } catch (err) {
          console.error(
            `Failed to email ${trainee.email}:`,
            err instanceof Error ? err.message : String(err),
          );
        }
      }),
    );
  };

  const handleUpdateAttendance = async () => {
    if (!selectedTraining?.id) return;

    const updates = traineeRows
      .map((trainee) => ({
        traineeId: trainee.traineeId,
        attendance: attendance[trainee.traineeId] as "Present" | "Absent",
        testCategory: selectedCategory,
      }))
      .filter(
        (update) =>
          update.attendance !== undefined &&
          update.attendance !== traineeRows.find(
            (t) => t.traineeId === update.traineeId,
          )?.attendance[selectedCategory],
      );

    if (updates.length === 0) {
      setShowAttendancePopup(false);
      return;
    }

    try {
      await updateAttendance(
        selectedTraining.id,
        updates,
        selectedTraining.tenantId,
      );
      await sendEmailsToPresentTrainees();
      setShowAttendancePopup(false);
      // Refresh so the grid/header reflect the just-saved attendance.
      fetchTrainings({
        type: "all",
        where: isFixedCategory
          ? { moduleType, category: validCategories[0] }
          : { moduleType },
      });
    } catch (err) {
      console.error("Failed to update attendance:", err);
    }
  };

  const handleOfflineTraineeSelect = (traineeId: string) => {
    if (!selectedTest) return;
    navigate(`${paths.attemptTest(selectedTest.id)}?traineeId=${traineeId}`);
    setShowOfflineModal(false);
    setOfflineTraineeSearch("");
  };

  const offlineFilteredTrainees = (selectedTraining?.trainees || []).filter(
    (trainee: Trainee) => {
      const query = offlineTraineeSearch.trim().toLowerCase();
      if (!query) return true;
      return (
        trainee.name?.toLowerCase().includes(query) ||
        trainee.traineeEmployeeId?.toLowerCase().includes(query)
      );
    },
  );

  const columnDefs: ColDef<TraineeRow>[] = [
    { headerName: "ID", field: "traineeEmployeeId", flex: 1 },
    { headerName: "Name", field: "name", flex: 1 },
    {
      headerName: "Pre-Test Score",
      field: "preTestScore",
      flex: 1,
      valueFormatter: (params) =>
        params.value != null ? `${Math.round(params.value)}%` : "-",
    },
    {
      headerName: "Post-Test Score",
      field: "postTestScore",
      flex: 1,
      valueFormatter: (params) =>
        params.value != null ? `${Math.round(params.value)}%` : "-",
    },
    {
      headerName: "Pre-Test Attendance",
      field: "attendance",
      flex: 1,
      cellRenderer: (params: { data: TraineeRow }) =>
        params.data.attendance["Pre-Test"] === "Present" ? (
          <span className="text-green-500">✔</span>
        ) : (
          <span className="text-red-500">✘</span>
        ),
    },
    {
      headerName: "Post-Test Attendance",
      field: "attendance",
      flex: 1,
      cellRenderer: (params: { data: TraineeRow }) =>
        params.data.attendance["Post-Test"] === "Present" ? (
          <span className="text-green-500">✔</span>
        ) : (
          <span className="text-red-500">✘</span>
        ),
    },
  ];

  const allPresent =
    traineeRows.length > 0 &&
    traineeRows.every((t) => attendance[t.traineeId] === "Present");

  if (!isTrainerRole) return null;

  return (
    <div className="px-2 py-4 space-y-6 mt-14">
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <h2 className="text-2xl font-semibold">Start Test</h2>
        <div className="flex flex-col md:flex-row items-center gap-4 w-full md:w-auto">
          <button
            onClick={() => selectedTraining && setShowAttendancePopup(true)}
            disabled={!selectedTraining}
            className="rounded-full flex justify-center items-center px-4 py-2 text-lg font-semibold bg-[#28a745] text-white whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Mark Attendance for {selectedCategory}
          </button>
          <button
            onClick={() => selectedTest && setShowOfflineModal(true)}
            disabled={!selectedTest}
            title={
              selectedTest
                ? undefined
                : `Create the ${selectedCategory} first to submit offline answers`
            }
            className="rounded-full flex justify-center items-center px-4 py-2 text-lg font-semibold bg-blue-500 text-white hover:bg-blue-600 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <User className="mr-2 h-4 w-4" />
            Submit Offline Answers
          </button>
          <Link to={paths.dashboard}>
            <button className="rounded-full flex justify-center items-center px-4 py-2 text-lg font-semibold bg-[#61748e] text-white whitespace-nowrap">
              <ArrowLeft className="mr-2 h-4 w-4" />
              BACK
            </button>
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 bg-white px-4 py-3 shadow-xl">
        <div className="flex flex-col">
          <label className="text-lg text-black font-bold">Topic</label>
          <select
            value={selectedTrainingId}
            onChange={(e) => setSelectedTrainingId(e.target.value)}
            className="border-2 border-[#a5b7cf] p-2 rounded mt-1"
          >
            <option value="">Select Topic</option>
            {availableTrainings.map((t) => (
              <option key={t.id} value={t.id}>
                {t.topic}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col">
          <label className="text-lg text-black font-bold">Category</label>
          <select
            value={selectedCategory}
            onChange={(e) =>
              setSelectedCategory(e.target.value as TestCategory)
            }
            className="border-2 border-[#a5b7cf] p-2 rounded mt-1"
          >
            <option value="Pre-Test">Pre-Test</option>
            <option value="Post-Test">Post-Test</option>
          </select>
        </div>
        <div className="flex flex-col">
          <p className="text-lg text-black font-bold">Test Date</p>
          <p className="font-medium text-black mt-3">
            {formatDate(selectedTest?.date)}
          </p>
        </div>
        <div className="flex flex-col">
          <p className="text-lg text-black font-bold">Total Trainees</p>
          <p className="font-medium text-black mt-3">
            {selectedTraining?.trainees?.length || 0}
          </p>
        </div>
      </div>

      {!selectedTraining ? (
        <div className="text-center py-8 text-gray-500">
          Select a topic above to view trainees and mark attendance.
        </div>
      ) : (
        <div className="ag-theme-alpine">
          <AgGridReact
            rowData={traineeRows}
            columnDefs={columnDefs}
            rowHeight={50}
            headerHeight={65}
            paginationPageSize={20}
            pagination={true}
            domLayout="autoHeight"
            defaultColDef={{ sortable: true, resizable: true }}
            getRowId={(params) => params.data.traineeId}
            getRowStyle={() => ({
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              marginBottom: "8px",
              backgroundColor: "white",
            })}
          />
        </div>
      )}

      {showAttendancePopup && selectedTraining && (
        <div className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm flex justify-center items-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden">
            <div className="p-8 space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-semibold text-gray-800">
                  Mark Attendance for {selectedCategory}
                </h2>
                <label className="relative inline-block w-16 h-7 ml-4">
                  <input
                    type="checkbox"
                    checked={allPresent}
                    onChange={(e) =>
                      markAll(e.target.checked ? "Present" : "Absent")
                    }
                    className="sr-only peer"
                  />
                  <div
                    className={`w-full h-full rounded-full transition-colors duration-300 ${
                      allPresent ? "bg-green-500" : "bg-red-400"
                    }`}
                  ></div>
                  <span
                    className={`absolute inset-y-0 left-0 w-7 h-7 bg-white rounded-full shadow-md flex items-center justify-center text-xs font-bold text-white transition-transform duration-300 ${
                      allPresent
                        ? "translate-x-9 bg-green-500"
                        : "translate-x-0 bg-red-400"
                    }`}
                  >
                    {allPresent ? "All P" : "All A"}
                  </span>
                </label>
              </div>
              <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                {traineeRows.map((trainee) => (
                  <div
                    key={trainee.traineeId}
                    className="flex justify-between items-center border border-gray-300 rounded-xl px-4 py-3 hover:shadow-md transition-shadow bg-gray-50"
                  >
                    <span className="text-gray-800 font-medium">
                      {trainee.name}
                    </span>
                    <label className="relative inline-block w-12 h-6">
                      <input
                        type="checkbox"
                        checked={attendance[trainee.traineeId] === "Present"}
                        onChange={() =>
                          toggleAttendance(
                            trainee.traineeId,
                            attendance[trainee.traineeId] === "Present"
                              ? "Absent"
                              : "Present",
                          )
                        }
                        className="sr-only peer"
                      />
                      <div
                        className={`w-full h-full rounded-full transition-colors duration-300 ${
                          attendance[trainee.traineeId] === "Present"
                            ? "bg-green-500"
                            : "bg-red-400"
                        }`}
                      ></div>
                      <span
                        className={`absolute inset-y-0 left-0 w-6 h-6 bg-white rounded-full shadow-md flex items-center justify-center text-xs font-bold text-white transition-transform duration-300 ${
                          attendance[trainee.traineeId] === "Present"
                            ? "translate-x-6 bg-green-500"
                            : "translate-x-0 bg-red-400"
                        }`}
                      >
                        {attendance[trainee.traineeId] === "Present" ? "P" : "A"}
                      </span>
                    </label>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-4 bg-[#5a687c] px-8 py-4">
              <button
                onClick={() => setShowAttendancePopup(false)}
                className="px-6 py-2 rounded-full bg-gray-200 text-gray-700 hover:bg-gray-300 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateAttendance}
                className="px-6 py-2 rounded-full bg-[#0fa9a6] text-white hover:bg-teal-700 transition"
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      {showOfflineModal && selectedTraining && (
        <div className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="p-6 space-y-4">
              <h3 className="text-lg font-semibold text-gray-800">
                Submit Offline Answers — {selectedCategory}
              </h3>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search trainees"
                  value={offlineTraineeSearch}
                  onChange={(e) => setOfflineTraineeSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border-2 border-[#a5b7cf] rounded-full text-sm focus:outline-none"
                />
              </div>
              <ul className="max-h-60 overflow-y-auto space-y-1">
                {offlineFilteredTrainees.length > 0 ? (
                  offlineFilteredTrainees.map((trainee: Trainee) => (
                    <li
                      key={trainee.traineeId}
                      onClick={() =>
                        handleOfflineTraineeSelect(trainee.traineeId)
                      }
                      className="p-2 rounded-lg hover:bg-gray-100 cursor-pointer"
                    >
                      {trainee.name}
                      {trainee.traineeEmployeeId
                        ? ` (${trainee.traineeEmployeeId})`
                        : ""}
                    </li>
                  ))
                ) : (
                  <li className="p-2 text-gray-500">No trainees found</li>
                )}
              </ul>
            </div>
            <div className="flex justify-end gap-4 bg-[#5a687c] px-6 py-4">
              <button
                onClick={() => {
                  setShowOfflineModal(false);
                  setOfflineTraineeSearch("");
                }}
                className="px-6 py-2 rounded-full bg-gray-200 text-gray-700 hover:bg-gray-300 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainerStartTest;
